import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
});

export default function BalanceSheetForm({ id, open, onClose, onUpdated }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [record, setRecord] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [values, setValues] = useState({});
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });

  const loadOptions = useCallback(async () => {
    try {
      const [cRes, iRes] = await Promise.all([
        api.get("/api/customer"),
        api.get("/api/item"),
      ]);
      setCustomers(
        (cRes.data || []).filter((c) =>
          ["supplier", "suppliers"].includes((c?.type ?? "").toLowerCase())
        )
      );
      setItems(iRes.data || []);
    } catch {
      setToast({ open: true, msg: "Failed loading options", sev: "error" });
    }
  }, []);

  const loadRecord = useCallback(async () => {
    try {
      const res = await api.get(`/api/balance-sheet/${id}`);
      setRecord(res.data);
      setValues({
        agentName: res.data.agentName,
        customerId: String(res.data.customerId),
        itemId: String(res.data.itemId),
        date: res.data.date,
        invoiceAmount: res.data.invoiceAmount,
        bankDeposit: res.data.bankDeposit,
        withHold: res.data.withHold,
        adjustment: res.data.adjustment,
        endingBalance: res.data.endingBalance,
        type: res.data.type,
      });
    } catch {
      setToast({ open: true, msg: "Failed loading data", sev: "error" });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (open) {
      loadOptions();
      loadRecord();
    }
  }, [open, loadOptions, loadRecord]);

  const handleChange = (name) => (e) =>
    setValues((v) => ({ ...v, [name]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/balance-sheet/${id}`, values);
      setToast({ open: true, msg: "Updated successfully", sev: "success" });
      onUpdated();
      onClose();
    } catch {
      setToast({ open: true, msg: "Update failed", sev: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit Balance Sheet</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Agent Name"
                  value={values.agentName || ""}
                  onChange={handleChange("agentName")}
                  fullWidth
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Customer"
                  value={values.customerId || ""}
                  onChange={handleChange("customerId")}
                  fullWidth
                  variant="filled"
                >
                  {customers.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Item"
                  value={values.itemId || ""}
                  onChange={handleChange("itemId")}
                  fullWidth
                  variant="filled"
                >
                  {items.map((i) => (
                    <MenuItem key={i.id} value={String(i.id)}>
                      {i.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date"
                  type="date"
                  value={values.date || ""}
                  onChange={handleChange("date")}
                  fullWidth
                  variant="filled"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Invoice Amount"
                  type="number"
                  value={values.invoiceAmount || ""}
                  onChange={handleChange("invoiceAmount")}
                  fullWidth
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Bank Deposit"
                  type="number"
                  value={values.bankDeposit || ""}
                  onChange={handleChange("bankDeposit")}
                  fullWidth
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="WithHold"
                  type="number"
                  value={values.withHold || ""}
                  onChange={handleChange("withHold")}
                  fullWidth
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Adjustment"
                  type="number"
                  value={values.adjustment || ""}
                  onChange={handleChange("adjustment")}
                  fullWidth
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Ending Balance"
                  type="number"
                  value={values.endingBalance || ""}
                  onChange={handleChange("endingBalance")}
                  fullWidth
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Type"
                  value={values.type || ""}
                  onChange={handleChange("type")}
                  fullWidth
                  variant="filled"
                >
                  <MenuItem value="debit">Debit</MenuItem>
                  <MenuItem value="credit">Credit</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="secondary"
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
         anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert severity={toast.sev} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
